import { useMemo, useRef } from 'react'
import { useLatestRef } from './useLatestRef'

const DELAY_MS = 500
const MOVE_THRESHOLD_PX = 10

export interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onClickCapture: (e: React.MouseEvent) => void
  onContextMenu: (e: React.MouseEvent) => void
}

// Long-press gesture for elements that also handle taps. Fires after a 500ms
// hold; movement past a small threshold (or the browser claiming the gesture
// for scrolling, via pointercancel) aborts it. The click that follows a
// completed long-press is swallowed in the capture phase so the element's
// normal tap action doesn't also run.
//
// The handlers stay attached even while no callback is passed, because the
// callback commonly goes away as a result of firing (entering trim mode drops
// `onLongPress`); detaching them then would drop the capture handler before
// the release click arrives, letting that click act as a tap (#434).
export function useLongPress(onLongPress?: () => void): LongPressHandlers {
  const callbackRef = useLatestRef(onLongPress)

  // In-flight gesture state. A ref rather than closure variables so the
  // handlers stay a stable memoized object while still being mutable from the
  // pointer callbacks that run long after the render that built them.
  const gesture = useRef<{
    timer: number | null
    start: { x: number; y: number } | null
    fired: boolean
  }>({ timer: null, start: null, fired: false })

  const handlers = useMemo<LongPressHandlers>(() => {
    const cancel = () => {
      const g = gesture.current
      if (g.timer !== null) {
        clearTimeout(g.timer)
        g.timer = null
      }
      g.start = null
    }

    return {
      onPointerDown: e => {
        const g = gesture.current
        g.fired = false
        if (!callbackRef.current) return
        g.start = { x: e.clientX, y: e.clientY }
        g.timer = window.setTimeout(() => {
          g.timer = null
          g.fired = true
          navigator.vibrate?.(50)
          callbackRef.current?.()
        }, DELAY_MS)
      },
      onPointerMove: e => {
        const { start } = gesture.current
        if (!start) return
        const dx = e.clientX - start.x
        const dy = e.clientY - start.y
        if (dx * dx + dy * dy > MOVE_THRESHOLD_PX * MOVE_THRESHOLD_PX) cancel()
      },
      onPointerUp: cancel,
      onPointerCancel: () => {
        cancel()
        gesture.current.fired = false
      },
      onClickCapture: e => {
        if (!gesture.current.fired) return
        gesture.current.fired = false
        e.preventDefault()
        e.stopPropagation()
      },
      onContextMenu: e => {
        if (callbackRef.current) e.preventDefault()
      },
    }
  }, [callbackRef])

  return handlers
}
